import { AxiosError } from 'axios'
import { translate } from '../../../core'
import { BadRequestException, UnauthorizedException } from '../../../core/type'
import { ChatMessage, ChatTurn } from './type/ai-assistant'

/** The backend rejects any history message longer than this (MAX_MESSAGE_LENGTH). */
export const MAX_MESSAGE_CHARS = 4000

/** The backend keeps the last 20 messages; send at most 9 prior exchanges + the new question. */
export const MAX_PRIOR_MESSAGES = 18

/** Only these replies carry context worth sending back; errors and "not configured" do not. */
const CONTEXT_VERDICTS = ['answered', 'clarify', 'refused']

export const clip = (text: string, max = MAX_MESSAGE_CHARS): string =>
  text.length <= max ? text : `${text.slice(0, max - 1)}…`

/**
 * The conversation to send for a new question: every completed exchange (the
 * question, the answer, and the SQL behind it), most recent first to go, then
 * the new question. Long answers are clipped so the request stays valid.
 */
export function buildHistory(turns: ChatTurn[], question: string): ChatMessage[] {
  const prior = turns
    .filter((t) => t.response && CONTEXT_VERDICTS.includes(t.response.verdict) && t.response.answer.trim())
    .flatMap((t): ChatMessage[] => {
      const sql = t.response?.result?.sql
      return [
        { role: 'user', content: clip(t.question) },
        { role: 'assistant', content: clip(t.response?.answer ?? ''), ...(sql ? { sql: clip(sql) } : {}) },
      ]
    })
  return [...prior.slice(-MAX_PRIOR_MESSAGES), { role: 'user', content: clip(question) }]
}

/** A readable message for a request that failed before the agent could answer. */
export function requestErrorMessage(error: unknown): string {
  if (error instanceof UnauthorizedException) return translate('ai_reports_session_expired')
  if (error instanceof BadRequestException) {
    const message = error.message
    return Array.isArray(message) ? message.join(' ') : String(message ?? translate('ai_reports_request_failed'))
  }
  const status = (error as AxiosError)?.response?.status
  if (status === 403) return translate('ai_reports_admin_only')
  if (status === 429) return translate('ai_reports_rate_limited')
  if (status === 404) return translate('ai_reports_gone')
  return translate('ai_reports_request_failed')
}

/** How a result cell is shown in the table. */
export function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** A file name (also used as the Excel sheet name, max 31 chars) stamped with local time. */
export function exportFileName(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `ai-report-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
}
